import React from 'react';
import { Box, Typography, useTheme, useMediaQuery, Paper, Grow, Container, Grid } from '@mui/material';
import { motion } from 'framer-motion';

export const ProcessStep = ({ 
  title, 
  description, 
  icon: Icon,
  color, 
  step,
  index
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  return (
    <Grow
      in={true}
      timeout={700 + (index * 150)}
    >
      <Grid sx={{mb:10}} item xs={12} sm={6} md={4}>
        <Box 
          sx={{ 
            position: 'relative',
            mb: { xs: 8, md: 4 }, // Add more bottom margin, especially on mobile
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 * index, ease: "easeOut" }}
            whileHover={{ 
              scale: 1.02,
              transition: { duration: 0.3 }
            }}
            style={{ height: '100%' }}
          >
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 2,
                transition: 'all 0.3s ease',
                borderLeft: `4px solid ${color}`,
                height: '100%',
                minHeight: 250,
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                '&:hover': {
                  boxShadow: `0 6px 16px ${color}30`,
                  transform: 'translateY(-4px)'
                }
              }}
            >
              {/* Number Circle - Positioned at the top */}
              <Box
                sx={{
                  position: 'absolute',
                  top: -25,
                  left: 'calc(50% - 25px)',
                  width: 50,
                  height: 50,
                  borderRadius: '50%',
                  bgcolor: color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1.25rem',
                  zIndex: 5,
                  boxShadow: `0 4px 10px ${color}50`,
                  border: '3px solid white'
                }}
              >
                {step}
              </Box>
              
              {/* Content */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  pt: 3,
                  height: '100%'
                }}
              >
                {/* Icon Circle */}
                <motion.div
                  whileHover={{ rotate: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  <Box
                    sx={{
                      width: 70,
                      height: 70,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${color}20, ${color}30)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                      mt: 2,
                      color,
                      boxShadow: `0 4px 8px ${color}20`,
                      flexShrink: 0
                    }}
                  >
                    {Icon && <Icon fontSize="large" />}
                  </Box>
                </motion.div>
                
                <Typography 
                  variant={isTablet ? "h6" : "h5"} 
                  sx={{ 
                    mb: 2,
                    fontWeight: 600,
                    color: theme.palette.mode === 'dark' ? 'white' : 'text.primary',
                    position: 'relative',
                    textAlign: 'center',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -8,
                      left: '25%',
                      width: '50%',
                      height: 3,
                      backgroundColor: color,
                      borderRadius: 2
                    }
                  }}
                >
                  {title}
                </Typography>
                
                <Typography 
                  variant="body1" 
                  color="text.secondary"
                  align="center"
                  sx={{
                    lineHeight: 1.7,
                    opacity: 0.9,
                    flexGrow: 1
                  }}
                >
                  {description}
                </Typography>
              </Box>
            </Paper>
          </motion.div>
        </Box>
      </Grid>
    </Grow>
  );
};

export const ProcessSteps = ({ steps }) => {
  return (
    <Container maxWidth="xl" sx={{ py: 5 }}>
      <Grid 
        container 
        spacing={4}
        alignItems="stretch"
        sx={{ 
          position: 'relative',
          py: 4 // Add padding to container to prevent cut-off
          
        }}
      >
        {steps.map((step, index) => (
          <ProcessStep
            key={index}
            {...step}
            index={index}
          />
        ))}
      </Grid>
    </Container>
  );
};

// Example usage
/*
import CodeIcon from '@mui/icons-material/Code';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import DeveloperModeIcon from '@mui/icons-material/DeveloperMode';
import BugReportIcon from '@mui/icons-material/BugReport';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

const processSteps = [
  {
    title: "Discovery & Planning",
    description: "We conduct in-depth research to understand your business goals, target audience, and project requirements.",
    icon: CodeIcon,
    color: "#8A2BE2",
    step: 1
  },
  {
    title: "Design & Prototyping",
    description: "Our designers create wireframes and interactive prototypes to visualize the user experience and interface.",
    icon: DesignServicesIcon,
    color: "#00CED1",
    step: 2
  },
  {
    title: "Development & Testing",
    description: "Our development team transforms designs into functional, responsive, and optimized code with rigorous testing.",
    icon: DeveloperModeIcon,
    color: "#00FA9A",
    step: 3
  },
  {
    title: "User Acceptance Testing",
    description: "We validate the product with actual users and stakeholders to ensure it meets all requirements and expectations.",
    icon: BugReportIcon,
    color: "#FFA500",
    step: 4
  },
  {
    title: "Deployment & Launch",
    description: "We ensure smooth deployment, conduct final quality checks, and prepare for a successful launch.",
    icon: RocketLaunchIcon,
    color: "#FFD700",
    step: 5
  },
  {
    title: "Maintenance & Support",
    description: "Our commitment extends beyond launch with ongoing support, updates, and continuous improvements.",
    icon: SupportAgentIcon,
    color: "#FF6B81",
    step: 6
  }
];

<ProcessSteps steps={processSteps} />
*/